import { useState } from 'react';
import type {
  FutebolStats, FutebolIncidente, FutebolFunFact, FutebolLineup, FutebolLineupTime,
} from '@/shared/types';

export function TelaCarregando() {
  return (
    <div className="flex-1 flex items-center justify-center">
      <div className="text-center">
        <div className="w-10 h-10 border-2 border-orange-500 border-t-transparent rounded-full animate-spin mx-auto mb-3" />
        <p className="text-white/50 text-sm">Conectando...</p>
      </div>
    </div>
  );
}

function BaraStat({ label, valorCasa, valorVisitante, sufixo = '' }: {
  label: string; valorCasa: number; valorVisitante: number; sufixo?: string;
}) {
  const total   = valorCasa + valorVisitante;
  const pctCasa = total > 0 ? Math.round((valorCasa / total) * 100) : 50;
  return (
    <div className="space-y-1">
      <div className="flex justify-between items-center">
        <span className="text-white font-bold text-sm tabular-nums">{valorCasa}{sufixo}</span>
        <span className="text-[11px] font-medium text-white/35">{label}</span>
        <span className="text-white font-bold text-sm tabular-nums">{valorVisitante}{sufixo}</span>
      </div>
      <div className="flex h-1.5 rounded-full overflow-hidden gap-px bg-white/[0.04]">
        <div className="bg-orange-500 rounded-l-full transition-all duration-700" style={{ width: `${pctCasa}%` }} />
        <div className="bg-white/20 rounded-r-full transition-all duration-700" style={{ width: `${100 - pctCasa}%` }} />
      </div>
    </div>
  );
}

export function EstatisticasCard({ stats, nomeCasa, nomeVisitante }: {
  stats: FutebolStats; nomeCasa: string; nomeVisitante: string;
}) {
  return (
    <div className="space-y-3 rounded-2xl border border-white/[0.06] bg-white/[0.02] p-4">
      <div className="mb-1 flex items-center justify-between">
        <span className="max-w-[38%] truncate text-[11px] font-medium text-white/45">{nomeCasa}</span>
        <span className="shrink-0 px-2 text-[11px] font-medium text-white/30">Estatísticas</span>
        <span className="max-w-[38%] truncate text-right text-[11px] font-medium text-white/45">{nomeVisitante}</span>
      </div>
      <BaraStat label="Posse"   valorCasa={stats.home.possession}       valorVisitante={stats.away.possession}       sufixo="%" />
      <BaraStat label="Chutes"  valorCasa={stats.home.total_shots}      valorVisitante={stats.away.total_shots} />
      <BaraStat label="Ataques" valorCasa={stats.home.dangerous_attack} valorVisitante={stats.away.dangerous_attack} />
    </div>
  );
}

function IconeIncidente({ inc }: { inc: FutebolIncidente }) {
  if (inc.type === 'goal') return <span className="text-base leading-none">⚽</span>;
  if (inc.type === 'card') {
    if (inc.card_type === 'red')       return <span className="inline-block w-3.5 h-4.5 rounded-sm bg-red-500 shrink-0" />;
    if (inc.card_type === 'yellowRed') return <span className="inline-block w-3.5 h-4.5 rounded-sm bg-gradient-to-br from-amber-400 to-red-500 shrink-0" />;
    return <span className="inline-block w-3.5 h-4.5 rounded-sm bg-amber-400 shrink-0" />;
  }
  return (
    <svg className="w-3.5 h-3.5 text-white/50 shrink-0" fill="none" viewBox="0 0 24 24" stroke="currentColor" strokeWidth={2}>
      <path strokeLinecap="round" strokeLinejoin="round" d="M8 7h12m0 0l-4-4m4 4l-4 4m0 6H4m0 0l4 4m-4-4l4-4" />
    </svg>
  );
}

function LinhaIncidente({ inc, nomeCasa, nomeVisitante }: {
  inc: FutebolIncidente; nomeCasa: string; nomeVisitante: string;
}) {
  const ehCasa   = inc.is_home;
  const nomeTime = ehCasa ? nomeCasa : nomeVisitante;
  let descricao  = '';
  if (inc.type === 'goal')         descricao = inc.player ?? '–';
  if (inc.type === 'card')         descricao = inc.player ?? '–';
  if (inc.type === 'substitution') descricao = `↑ ${inc.player_in ?? '–'}  ↓ ${inc.player_out ?? '–'}`;

  return (
    <div className={`flex items-center gap-3 py-2.5 px-4 ${ehCasa ? 'flex-row' : 'flex-row-reverse'}`}>
      <span className="text-white/50 font-mono text-[11px] w-7 shrink-0 text-center">{inc.minute}'</span>
      <div className="flex items-center gap-1.5 shrink-0">
        <IconeIncidente inc={inc} />
      </div>
      <div className={`flex-1 min-w-0 ${ehCasa ? 'text-left' : 'text-right'}`}>
        <p className="text-white/90 text-xs font-semibold truncate">{descricao}</p>
        <p className="text-white/50 text-[11px] truncate">{nomeTime}</p>
      </div>
    </div>
  );
}

export function IncidentesTimeline({ incidents, nomeCasa, nomeVisitante }: {
  incidents: FutebolIncidente[]; nomeCasa: string; nomeVisitante: string;
}) {
  const ordenados = [...incidents].sort((a, b) => b.minute - a.minute);
  return (
    <div className="overflow-hidden rounded-2xl border border-white/[0.06] bg-white/[0.02]">
      <div className="flex items-center gap-2 border-b border-white/[0.05] px-4 py-3">
        <svg className="h-3.5 w-3.5 text-white/40" fill="none" viewBox="0 0 24 24" stroke="currentColor" strokeWidth={2}>
          <path strokeLinecap="round" strokeLinejoin="round" d="M12 8v4l3 3m6-3a9 9 0 11-18 0 9 9 0 0118 0z" />
        </svg>
        <span className="text-[11px] font-medium text-white/40">Lances</span>
      </div>
      <div className="divide-y divide-white/[0.04]">
        {ordenados.map((inc, i) => (
          <LinhaIncidente key={i} inc={inc} nomeCasa={nomeCasa} nomeVisitante={nomeVisitante} />
        ))}
      </div>
    </div>
  );
}

function formatarPreview(text: string): string {
  return text
    .replace(/#{1,6}\s+/gm, '')
    .replace(/\*\*(.+?)\*\*/g, '$1')
    .replace(/\*(.+?)\*/g, '$1')
    .replace(/^[-*]\s+/gm, '• ')
    .replace(/\n{3,}/g, '\n\n')
    .trim();
}

export function AiPreviewCard({ text }: { text: string }) {
  const [expandido, setExpandido] = useState(false);
  const conteudo = formatarPreview(text);
  const longo    = conteudo.length > 380;
  const visivel  = longo && !expandido ? conteudo.slice(0, 380) + '…' : conteudo;

  return (
    <div className="overflow-hidden rounded-2xl border border-white/[0.06] bg-white/[0.02]">
      <div className="flex items-center gap-2 border-b border-white/[0.05] px-4 py-3">
        <svg className="h-3.5 w-3.5 shrink-0 text-white/40" fill="none" viewBox="0 0 24 24" stroke="currentColor" strokeWidth={1.5}>
          <path strokeLinecap="round" strokeLinejoin="round" d="M9.813 15.904L9 18.75l-.813-2.846a4.5 4.5 0 00-3.09-3.09L2.25 12l2.846-.813a4.5 4.5 0 003.09-3.09L9 5.25l.813 2.846a4.5 4.5 0 003.09 3.09L15.75 12l-2.846.813a4.5 4.5 0 00-3.09 3.09zM18.259 8.715L18 9.75l-.259-1.035a3.375 3.375 0 00-2.455-2.456L14.25 6l1.036-.259a3.375 3.375 0 002.455-2.456L18 2.25l.259 1.035a3.375 3.375 0 002.456 2.456L21.75 6l-1.035.259a3.375 3.375 0 00-2.456 2.456z" />
        </svg>
        <span className="text-[11px] font-medium text-white/40">Análise</span>
      </div>
      <div className="px-4 py-3">
        <p className="text-white/70 text-xs leading-relaxed whitespace-pre-line">{visivel}</p>
        {longo && (
          <button onClick={() => setExpandido(e => !e)} className="mt-2 min-h-11 text-orange-400 text-[11px] font-semibold active:opacity-70">
            {expandido ? 'Ver menos' : 'Ver mais'}
          </button>
        )}
      </div>
    </div>
  );
}

type PosicaoKey = 'G' | 'D' | 'M' | 'F';
const POSICAO_LABEL: Record<PosicaoKey, string> = { G: 'Gol', D: 'Def', M: 'Meio', F: 'Ata' };
const POSICAO_ORDEM: PosicaoKey[] = ['G', 'D', 'M', 'F'];

function ColunaTitulares({ time, invertido = false }: { time: FutebolLineupTime; invertido?: boolean }) {
  const grupos = POSICAO_ORDEM
    .map(pos => ({ pos, jogadores: time.players.filter(p => p.position === pos) }))
    .filter(g => g.jogadores.length > 0);

  return (
    <div className={`flex-1 min-w-0 space-y-2.5 ${invertido ? 'items-end' : ''}`}>
      {grupos.map(({ pos, jogadores }) => (
        <div key={pos}>
          <p className={`mb-1 text-[11px] font-medium text-white/30 ${invertido ? 'text-right' : ''}`}>
            {POSICAO_LABEL[pos as PosicaoKey]}
          </p>
          {jogadores.map(p => (
            <div key={p.id} className={`flex items-center gap-1 ${invertido ? 'flex-row-reverse' : ''}`}>
              <span className="text-white/50 font-mono text-[11px] w-5 shrink-0 text-center">{p.jersey_number}</span>
              <span className={`text-white/90 text-[11px] truncate ${invertido ? 'text-right' : ''}`}>{p.short_name}</span>
            </div>
          ))}
        </div>
      ))}
    </div>
  );
}

export function EscalacaoCard({ lineup, nomeCasa, nomeVisitante }: {
  lineup: FutebolLineup; nomeCasa: string; nomeVisitante: string;
}) {
  if (lineup.status === 'unavailable' || !lineup.home || !lineup.away) return null;
  const confirmada = lineup.status === 'confirmed';

  return (
    <div className="overflow-hidden rounded-2xl border border-white/[0.06] bg-white/[0.02]">
      <div className="flex items-center justify-between border-b border-white/[0.05] px-4 py-3">
        <div className="flex items-center gap-2">
          <svg className="h-3.5 w-3.5 text-white/40" fill="none" viewBox="0 0 24 24" stroke="currentColor" strokeWidth={2}>
            <path strokeLinecap="round" strokeLinejoin="round" d="M17 20h5v-2a3 3 0 00-5.356-1.857M17 20H7m10 0v-2c0-.656-.126-1.283-.356-1.857M7 20H2v-2a3 3 0 015.356-1.857M7 20v-2c0-.656.126-1.283.356-1.857m0 0a5.002 5.002 0 019.288 0M15 7a3 3 0 11-6 0 3 3 0 016 0z" />
          </svg>
          <span className="text-[11px] font-medium text-white/40">Escalação</span>
        </div>
        <span className={`rounded-md px-2 py-0.5 text-[10px] font-bold ring-1 ${
          confirmada
            ? 'bg-emerald-500/15 text-emerald-400 ring-emerald-500/30'
            : 'bg-amber-500/15 text-amber-400 ring-amber-500/25'
        }`}>
          {confirmada ? 'Confirmada' : 'Prevista'}
        </span>
      </div>
      <div className="flex items-center justify-between border-b border-white/[0.03] px-4 py-2">
        <span className="font-mono text-xs font-bold text-white/50">{lineup.home.formation}</span>
        <span className="text-[11px] font-medium text-white/30">Formação</span>
        <span className="font-mono text-xs font-bold text-white/50">{lineup.away.formation}</span>
      </div>
      <div className="flex items-center justify-between px-4 py-1.5 border-b border-white/[0.03]">
        <span className="text-white/50 text-[11px] font-semibold truncate max-w-[44%]">{nomeCasa}</span>
        <span className="text-white/50 text-[11px] font-semibold truncate max-w-[44%] text-right">{nomeVisitante}</span>
      </div>
      <div className="flex gap-3 px-4 py-3">
        <ColunaTitulares time={lineup.home} />
        <div className="w-px bg-white/[0.05] shrink-0" />
        <ColunaTitulares time={lineup.away} invertido />
      </div>
      {(lineup.unavailable_home.length > 0 || lineup.unavailable_away.length > 0) && (
        <div className="px-4 pb-3 border-t border-white/[0.04]">
          <p className="mb-2 mt-3 text-[11px] font-medium text-white/30">Indisponíveis</p>
          <div className="flex gap-3">
            <div className="flex-1 space-y-1">
              {lineup.unavailable_home.map(p => (
                <div key={p.id} className="flex items-center gap-1.5">
                  <span className="text-rose-500/60 text-[11px] shrink-0">✕</span>
                  <span className="text-white/50 text-[11px] truncate">{p.short_name}</span>
                </div>
              ))}
            </div>
            <div className="w-px bg-white/[0.05] shrink-0" />
            <div className="flex-1 space-y-1">
              {lineup.unavailable_away.map(p => (
                <div key={p.id} className="flex items-center gap-1.5 flex-row-reverse">
                  <span className="text-rose-500/60 text-[11px] shrink-0">✕</span>
                  <span className="text-white/50 text-[11px] truncate text-right">{p.short_name}</span>
                </div>
              ))}
            </div>
          </div>
        </div>
      )}
    </div>
  );
}

export function FunFactsCard({ funfacts }: { funfacts: FutebolFunFact[] }) {
  return (
    <div className="overflow-hidden rounded-2xl border border-white/[0.06] bg-white/[0.02]">
      <div className="flex items-center gap-2 border-b border-white/[0.05] px-4 py-3">
        <svg className="h-3.5 w-3.5 shrink-0 text-white/40" fill="none" viewBox="0 0 24 24" stroke="currentColor" strokeWidth={1.5}>
          <path strokeLinecap="round" strokeLinejoin="round" d="M12 18v-5.25m0 0a6.01 6.01 0 001.5-.189m-1.5.189a6.01 6.01 0 01-1.5-.189m3.75 7.478a12.06 12.06 0 01-4.5 0m3.75 2.355a3 3 0 01-3 0M10.5 7.5a1.5 1.5 0 113 0 1.5 1.5 0 01-3 0zM12 10.5V18" />
        </svg>
        <span className="text-[11px] font-medium text-white/40">Curiosidades</span>
      </div>
      <div className="divide-y divide-white/[0.04]">
        {funfacts.map((f, i) => (
          <p key={i} className="px-4 py-3 text-white/70 text-xs leading-relaxed">{f.sentence}</p>
        ))}
      </div>
    </div>
  );
}
